/**
 * @see https://docs.google.com/spreadsheets/d/1FNN_n0FiP49GCljsiqg1CMwv99KU6wWEloWiet3t_j8/edit#gid=583702960
 */

import { type Collections, type ScopedPermissions, type Workspaces } from '@air/api';
import { type ShortUrlObjectType } from '@air/api/types';
import { type ApiResponse } from '@air/utils-api';

import { canCreateCollection, canEditCollection } from '~/utils/permissions/workspacePermissions';

export const canViewShortUrl = (sourceContext: ScopedPermissions | undefined) => !!sourceContext?.shortUrl.canView;

export const canCreateShortUrl = (sourceContext: ScopedPermissions | undefined) => !!sourceContext?.shortUrl.canCreate;

export const canEditShortUrl = (shortUrlContext: ScopedPermissions | undefined) => !!shortUrlContext?.shortUrl.canEdit;

export const canDeleteShortUrl = (shortUrlContext: ScopedPermissions | undefined) =>
  !!shortUrlContext?.shortUrl.canDelete;

type CollectionFromGet = ApiResponse<typeof Collections.get>;
type ShortUrlListItem = ApiResponse<typeof Workspaces.ShortUrl.list>['data'][number];
type CollectionShortUrlListItem = Extract<ShortUrlListItem, { objectType: ShortUrlObjectType.collection }>;
type CollectionFromShortUrlList = CollectionShortUrlListItem['collection'];

/**
 * The ownership-identity fields ownership resolution may read — the input contract of
 * `getCollectionAccountIds`. The two collection-returning surfaces spell the owner differently
 * (`Collections.get` carries `createdBy`/`ownedBy` account ids; a `Workspaces.ShortUrl.list` row's
 * embedded collection carries `creator`/`owner` objects), so each member picks its own spelling.
 * Only this module knows which fields matter to ownership; callers compose this type instead of
 * enumerating fields themselves.
 */
export type CollectionOwnershipSource =
  | Pick<CollectionFromGet, 'createdBy' | 'ownedBy'>
  | Pick<CollectionFromShortUrlList, 'creator' | 'owner'>;

const addAccountId = (accountIds: Set<string>, value: unknown) => {
  if (typeof value === 'string' && value) {
    accountIds.add(value);
  }
};

const getCollectionAccountIds = (collection: CollectionOwnershipSource | null | undefined) => {
  const accountIds = new Set<string>();

  if (!collection) {
    return accountIds;
  }

  const creator = 'creator' in collection ? collection.creator : undefined;

  if (creator && typeof creator === 'object' && 'id' in creator) {
    addAccountId(accountIds, creator.id);
  }

  if ('createdBy' in collection) {
    addAccountId(accountIds, collection.createdBy);
  }

  const owner = 'owner' in collection ? collection.owner : undefined;

  if (owner && typeof owner === 'object' && 'id' in owner) {
    addAccountId(accountIds, owner.id);
  }

  if ('ownedBy' in collection) {
    addAccountId(accountIds, collection.ownedBy);
  }

  return accountIds;
};

export const isCollectionOwner = (
  collection: CollectionOwnershipSource | null | undefined,
  accountId: string | null | undefined,
) => {
  if (!accountId) {
    return false;
  }

  return getCollectionAccountIds(collection).has(accountId);
};

export const canEditCollectionShareLink = (
  workspacePermissions: ScopedPermissions | undefined,
  collection: CollectionOwnershipSource | null | undefined,
  accountId: string | null | undefined,
) => {
  return canEditCollection(workspacePermissions) || isCollectionOwner(collection, accountId);
};

type ShowExistingShowcasesMenuSectionParams = {
  workspacePermissions: ScopedPermissions | undefined;
  editableShortUrlsCount: number;
  isFetching: boolean;
  debouncedSearch: string;
};

export const shouldShowExistingShowcasesMenuSection = ({
  workspacePermissions,
  editableShortUrlsCount,
  isFetching,
  debouncedSearch,
}: ShowExistingShowcasesMenuSectionParams) => {
  return (
    canEditCollection(workspacePermissions) ||
    canCreateCollection(workspacePermissions) ||
    editableShortUrlsCount > 0 ||
    isFetching ||
    debouncedSearch.trim().length > 0
  );
};
